import { CartIcon, EditIcon, EllipsisIcon, ImageIcon, ShoppingBagIcon, TrashIcon } from "@xola/icons";
import { flash, PopoverList } from "@xola/ui-kit";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { ButtonType, Package, Permission } from "types";
import { api } from "../../../api/api";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { ProductImage } from "../../Common/ProductImage";
import { PromptModal } from "../../Common/PromptModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { useSeller, useUser } from "@/hooks/useAccount";
import { getButtonUrl, isX2CheckoutSupported, openCheckoutIframe } from "@/models/ButtonCode";
import { isXolaAdmin } from "@/models/User";

export const PartnerPackageTile = ({ partnerPackage }: { partnerPackage: Package }) => {
    const history = useHistory();
    const { isGranted } = useAuthorization();

    const handleClick = () => {
        if (!isGranted(Permission.PARTNER_PACKAGES_MODIFY)) {
            return;
        }

        history.push(`/products/partnerPackages/${partnerPackage.id}/description`);
    };

    return (
        <div className="group flex cursor-pointer rounded p-2" data-testid="package-tile" onClick={handleClick}>
            <ProductImage product={partnerPackage} size="medium" />
            <div className="w-full">
                <div className="min-h-8 flex justify-between">
                    <NoTranslate className="text-lg font-bold line-clamp-2">{partnerPackage.name}</NoTranslate>
                    <IsGranted permissions={Permission.PARTNER_PACKAGES_MODIFY}>
                        <div className="flex min-w-24 justify-end space-x-2">
                            <SecondaryButton size="small" className="invisible group-hover:visible" icon={<EditIcon />}>
                                Edit
                            </SecondaryButton>
                            <PackageActionButton product={partnerPackage} />
                        </div>
                    </IsGranted>
                </div>
                <NoTranslate className="pt-1 text-base line-clamp-2" data-testid="package-tile-desc">
                    {partnerPackage.excerpt}
                </NoTranslate>
            </div>
        </div>
    );
};

const PackageActionButton = ({ product }: { product: Package }) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [visible, setVisible] = useState(false);
    const invalidate = useInvalidate();
    const history = useHistory();
    const user = useUser();
    const seller = useSeller();
    const showX2Checkout = isX2CheckoutSupported({ type: ButtonType.SINGLE }, product);

    // TODO: use useModal for this (same in packages tile as well)
    const show = (e: any) => {
        e.stopPropagation();
        setVisible(true);
    };

    const hide = (e: any) => {
        if ("hide" in e) {
            e.hide();
        } else {
            e.stopPropagation();
        }

        setVisible(false);
    };

    const { mutateAsync: deletePackage, isLoading } = useMutation(async (id: ID) => {
        return api.delete(`/partnerPackages/${id}`);
    });

    const handleDelete = async (e: any) => {
        e.stopPropagation();
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (product?.id) {
            await deletePackage(product.id);
            invalidate("partnerPackages", product.id);
            flash.show({ text: "Partner Package deleted", color: "success" });
        }

        setIsDeleteModalOpen(false);
    };

    const handleEditClick = async (e: any, section: string) => {
        e.stopPropagation();
        history.push(`/products/partnerPackages/${product.id}/${section}`);
    };

    const handleCheckout = (e: any) => {
        e.stopPropagation();
        const url = getButtonUrl(seller, "", ButtonType.SINGLE, product);
        if (url) {
            window.open(url, "_blank");
        }

        setVisible(false);
    };

    const handleX2Checkout = (e: any) => {
        e.stopPropagation();
        const url = getButtonUrl(seller, "", ButtonType.SINGLE + "_x2", product);
        setVisible(false);
        openCheckoutIframe(url);
    };

    return (
        <>
            <PopoverList visible={visible} zIndex="10" onClickOutside={hide}>
                <SecondaryButton size="small" onClick={visible ? hide : show}>
                    <EllipsisIcon />
                </SecondaryButton>

                <PopoverList.Item
                    name="Edit Partner Package"
                    onClickItem={async (e: any) => handleEditClick(e, "description")}
                >
                    <EditIcon />
                    <span>Edit Package</span>
                </PopoverList.Item>

                <PopoverList.Item name="Manage Photos" onClickItem={async (e: any) => handleEditClick(e, "photos")}>
                    <ImageIcon />
                    <span>Manage Photos</span>
                </PopoverList.Item>

                {(isXolaAdmin(user) || !showX2Checkout) && (
                    <PopoverList.Item name="Preview Checkout" onClickItem={handleCheckout}>
                        <ShoppingBagIcon className="top-0" />
                        <span>Preview Checkout</span>
                    </PopoverList.Item>
                )}

                {showX2Checkout && (
                    <PopoverList.Item name="Preview X2 Checkout" onClickItem={handleX2Checkout}>
                        <CartIcon className="top-0" />
                        <span>Preview {isXolaAdmin(user) ? "X2 " : ""}Checkout</span>
                    </PopoverList.Item>
                )}

                <PopoverList.Item name="Delete" onClickItem={handleDelete}>
                    <TrashIcon />
                    <span>Delete</span>
                </PopoverList.Item>
            </PopoverList>
            <PromptModal
                title="Delete Partner Package?"
                isLoading={isLoading}
                isOpen={isDeleteModalOpen}
                onConfirm={handleDeleteConfirm}
                onClose={() => setIsDeleteModalOpen(false)}
            >
                Are you sure you want to delete this partner package?
            </PromptModal>
        </>
    );
};
